import { Skeleton } from "antd";
import moment from "moment";
import React, { useEffect, useRef } from "react";
import DataTable from "react-data-table-component";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import TableNoContent from "@/Components/common/TableNoContent";
import { routingConstants } from "@/Constants/routingConstants";
import {
  deSelectAllListings,
  fetchExpandedWorkHistory,
  fetchWorkHistory,
  setLoading,
  updateSelectedWorkedHistory,
  updateWorkedHistoryPagination,
  updateWorkHistorySorting,
} from "@/reducers/clientcandidatedetail";
import {
  expandButtonColumn,
  getActionByHtml,
  getColumnNormalHtml,
  totalDaysColumn,
} from "@/utilities/datatable/columnFunctions";
import { sortByDate, sortByTime } from "@/utilities/DataTableSorting";
import useDebounceScroll from "@/utilities/hooks/useDebounceScroll";

import CoverIcon from "../../../../Icons/CoverIcon";
import WorkedShifts from "../../candidates/Listing/common/WorkedShifts";
import WorkedStatusButton from "../../candidates/Listing/Worked/WorkedStatusButton";
import ClientPayPopover from "../../dataTables/ClientPayPopover";

/**
 * React component for displaying work history listing.
 *
 * @return {JSX.Element} JSX element for the work history listing
 */
const WorkHistoryListing = () => {
  const {
    candidateDetails,
    candidateWorkedHistory,
    isLoading,
    workedHistoryFilters,
    workedHistoryPagination,
    workedHistoryView,
    toggleListingSelection,
    workHistorySorting,
  } = useSelector((state) => state.clientCandidateDetail);
  const { clientUserAllPermissions } = useSelector((state) => state.session);
  const dispatch = useDispatch();
  useEffect(() => {
    if (candidateDetails && candidateDetails.candidate_id) {
      dispatch(setLoading(true));
      dispatch(
        fetchWorkHistory({
          candidate_id: candidateDetails.candidate_id,
        }),
      );
    }
  }, [candidateDetails]);
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    const delayDebounceFn = setTimeout(() => {
      dispatch(setLoading(true));
      dispatch(
        updateWorkedHistoryPagination({
          ...workedHistoryPagination,
          currentPage: 1,
          isLastPage: false,
        }),
      );
      dispatch(
        fetchWorkHistory({
          candidate_id: candidateDetails.candidate_id,
        }),
      );
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [workedHistoryFilters.search]);
  const columns = [
    {
      name: "Ref No",
      sortable: true,
      sortField: "contract_ref_no",
      // sortFunction: (rowA, rowB) => sortByText(rowA, rowB, "contract_ref_no"),

      selector: (row) =>
        !clientUserAllPermissions ||
        clientUserAllPermissions.is_allow_to_view_contract_detail === 1 ? (
          <div className="flex flex-wrap items-center gap-1">
            <Link
              to={routingConstants.contractDetail(row.route_contract_id)}
              className="text-[14px] font-medium leading-[normal] text-txtDark"
            >
              {row.contract_ref_no}
            </Link>
            {row?.is_substitute_contract && workedHistoryView !== "shifts" ? (
              <div
                div
                className={`flex h-[25px] w-[37px] cursor-pointer items-center gap-2 rounded-[4px] bg-lightOrange px-2 py-1 text-center font-medium leading-[23px] text-darkOrange`}
              >
                <CoverIcon cls="text-darkOrange" />
              </div>
            ) : null}
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            <span className="line-clamp-1 cursor-default text-[12px] font-medium leading-[normal] text-txtColor">
              {row.contract_ref_no}
            </span>
            {row?.is_substitute_contract && workedHistoryView !== "shifts" ? (
              <div
                div
                className={`flex h-[25px] w-[37px] cursor-pointer items-center gap-2 rounded-[4px] bg-lightOrange px-2 py-1 text-center font-medium leading-[23px] text-darkOrange`}
              >
                <CoverIcon cls="text-darkOrange" />
                <span className={`text-[12px] text-darkOrange`}>{"Cover"}</span>
              </div>
            ) : null}
          </div>
        ),
      width: workedHistoryView === "shifts" ? "12%" : "18%",
    },

    ...(workedHistoryView === "shifts"
      ? [
          {
            name: "Actioned By",
            selector: (row) => (
              <div className="flex flex-col" data-tag="allowRowEvents">
                {
                  <div className="flex flex-col" data-tag="allowRowEvents">
                    {getActionByHtml(
                      row.timesheet_status === "Authorised"
                        ? row?.authorised_by_name
                        : row?.timesheet_status == "Rejected"
                          ? row.rejected_by_name
                          : row.timesheet_status == "Terminated"
                            ? row.terminated_by_name
                            : "-",
                      row.timesheet_status === "Authorised"
                        ? row?.authorised_at
                        : row?.timesheet_status == "Rejected"
                          ? row.rejected_at
                          : row?.timesheet_status == "Terminated"
                            ? row.terminated_at
                            : "-",
                      row.timesheet_status === "Authorised"
                        ? row?.authorised_from
                        : row?.timesheet_status == "Rejected"
                          ? row.rejected_from
                          : row?.timesheet_status == "Terminated"
                            ? row.terminated_from
                            : "-",
                      "",
                    )}
                  </div>
                }
              </div>
            ),
            width: "150px",
          },
          {
            name: "Date",
            sortable: true,
            sortField: "contract_date",
            // sortFunction: (rowA, rowB) =>
            //   sortByDate(
            //     moment(rowA.shift_date).format("DD/MM/YYYY"),
            //     moment(rowB.shift_date).format("DD/MM/YYYY"),
            //   ),
            selector: (row) => (
              <div
                className="flex flex-col items-end gap-1"
                data-tag="allowRowEvents"
              >
                <>
                  {getColumnNormalHtml(
                    moment(row.shift_date).format("DD/MM/YYYY"),
                  )}
                </>
              </div>
            ),
            width: "10.6%",
          },
          {
            name: "Start Time",
            sortable: true,
            sortField: "contract_start_time",
            // sortFunction: (rowA, rowB) =>
            //   sortByTime(
            //     moment(rowA.contract_start_time, "HH:mm:ss").format("HH:mm"),
            //     moment(rowB.contract_start_time, "HH:mm:ss").format("HH:mm"),
            //   ),
            selector: (row) =>
              getColumnNormalHtml(
                moment(row.contract_start_time, "HH:mm:ss").format("HH:mm"),
              ),
            width: "9%",
          },
          {
            name: "End Time",
            sortable: true,
            sortField: "contract_end_time",

            // sortFunction: (rowA, rowB) =>
            //   sortByTime(
            //     moment(rowA.contract_end_time, "HH:mm:ss").format("HH:mm"),
            //     moment(rowB.contract_end_time, "HH:mm:ss").format("HH:mm"),
            //   ),
            selector: (row) =>
              getColumnNormalHtml(
                moment(row.contract_end_time, "HH:mm:ss").format("HH:mm"),
              ),
            width: "8%",
          },
          {
            name: "Break",
            sortable: true,
            sortField: "un_paid_break_hour",
            sortFunction: (rowA, rowB) =>
              sortByTime(
                moment(rowA.break_time, "HH:mm:ss").format("HH:mm"),
                moment(rowB.break_time, "HH:mm:ss").format("HH:mm"),
              ),
            selector: (row) =>
              getColumnNormalHtml(
                moment(row.break_time, "HH:mm:ss").format("HH:mm"),
              ),
            width: "6.5%",
          },

          {
            name: "Admin",
            sortable: true,
            sortField: "contract_admin_time",
            // sortFunction: (rowA, rowB) =>
            //   sortByTime(
            //     moment(rowA.admin_time, "HH:mm:ss").format("HH:mm"),
            //     moment(rowB.admin_time, "HH:mm:ss").format("HH:mm"),
            //   ),
            selector: (row) =>
              getColumnNormalHtml(
                moment(row.admin_time, "HH:mm:ss").format("HH:mm"),
              ),
            width: "7.4%",
          },
          {
            name: "Visit",
            sortable: true,
            sortField: "contract_no_of_visit",
            // sortFunction: (rowA, rowB) => sortByNumber(rowA, rowB, "no_of_visit"),
            selector: (row) =>
              getColumnNormalHtml(row.no_of_visit ? row.no_of_visit : 0),
            width: "5%",
          },
          {
            name: "Unit",
            sortable: false,
            // sortFunction: (rowA, rowB) => sortByNumber(rowA, rowB, "contract_duration"),
            selector: (row) => (
              <div className="flex flex-col items-end">
                {row.contract_duration !== "-"
                  ? getColumnNormalHtml(
                      Number(`${row.contract_duration}`).toFixed(2),
                    )
                  : "0.00"}
              </div>
            ),
            width: "6%",
          },
          {
            name: "Total",
            sortable: false,
            // sortFunction: (rowA, rowB) => sortByNumber(rowA, rowB, "rate"),
            selector: (row) => (
              <ClientPayPopover
                pay_arr={row?.client_pay_arr}
                shift_rate={
                  Number(row.rate) +
                  Number(row.cover_request_awarded_reward || 0) -
                  Number(row.cover_request_awarded_penalty || 0)
                }
                is_substitute_contract={
                  row?.cover_request_awarded_reward != 0
                    ? 1
                    : row?.cover_request_awarded_penalty != 0
                      ? 1
                      : 0
                }
                cover_request_awarded_penalty={
                  row?.cover_request_awarded_penalty
                }
                cover_request_awarded_reward={row.cover_request_awarded_reward}
                content={(content) => (
                  <div
                    className="flex flex-col gap-1"
                    data-tag="allowRowEvents"
                  >
                    {getColumnNormalHtml(content, "cursor-pointer")}
                  </div>
                )}
              />
            ),
            width: "",
          },
          {
            name: "Status",
            sortable: true,
            sortField: "timesheet_status",
            // sortFunction: (rowA, rowB) => sortByText(rowA, rowB, "timesheet_status"),
            selector: (row) => (
              <div className="flex flex-col items-end gap-1">
                <WorkedStatusButton
                  timesheet_status={row.timesheet_status}
                  contract_ref_no={row.contract_ref_no}
                  btnClass="cursor-pointer rounded-md border px-2 text-[14px] font-medium leading-[24px]"
                />
              </div>
            ),
            width: "12.5%",
          },
        ]
      : [
          {
            name: "Start Date",
            sortable: true,
            sortFunction: (rowA, rowB) =>
              sortByDate(
                moment(rowA.contract_start_date).format("DD/MM/YYYY"),
                moment(rowB.contract_start_date).format("DD/MM/YYYY"),
              ),
            selector: (row) => (
              <div
                className="flex flex-col items-end gap-1"
                data-tag="allowRowEvents"
              >
                {getColumnNormalHtml(
                  moment(row.contract_start_date).format("DD/MM/YYYY"),
                )}
              </div>
            ),
            width: "17%",
          },
          {
            name: "End Date",
            sortable: true,
            sortFunction: (rowA, rowB) =>
              sortByDate(
                moment(rowA.contract_end_date).format("DD/MM/YYYY"),
                moment(rowB.contract_end_date).format("DD/MM/YYYY"),
              ),
            selector: (row) => (
              <div
                className="flex flex-col items-end gap-1"
                data-tag="allowRowEvents"
              >
                {getColumnNormalHtml(
                  moment(row.contract_end_date).format("DD/MM/YYYY"),
                )}
              </div>
            ),
            width: "17%",
          },
          totalDaysColumn("Total Days", "days_total", ""),
          totalDaysColumn("Booked", "days_booked", ""),
          totalDaysColumn("Worked", "days_worked", ""),
          expandButtonColumn(),
        ]),
  ];
  const wrapper = useRef(null);
  let height = wrapper?.current?.clientHeight
    ? wrapper?.current?.clientHeight - 66
    : 400;
  const dataTableRef = useRef(null);
  /**
   * Handle table scroll event
   */

  const handleDebounceScroll = useDebounceScroll(() =>
    dispatch(
      fetchWorkHistory({
        candidate_id: candidateDetails.candidate_id,
      }),
    ),
  );
  useEffect(() => {
    if (dataTableRef.current) {
      const table = dataTableRef.current.querySelector("._DataTable");
      table.addEventListener("scroll", handleDebounceScroll);
      return () => {
        table.removeEventListener("scroll", handleDebounceScroll);
      };
    }
  }, [handleDebounceScroll]);

  const Expandedrow = ({ data }) => (
    <WorkedShifts
      shifts={data.shifts}
      dateFormat="YYYY-MM-DD"
      substitueCon={data?.is_substitute_contract}
    />
  );

  const handleSort = ({ sortField: newSortField }) => {
    const { sortByColumn, sortByOrder } = workHistorySorting; // get from Redux

    let newSortOrder = "asc";
    if (sortByColumn === newSortField) {
      newSortOrder = sortByOrder === "asc" ? "desc" : "asc";
    }

    dispatch(
      updateWorkHistorySorting({
        sortByColumn: newSortField,
        sortByOrder: newSortOrder,
      }),
    );

    dispatch(deSelectAllListings());
    dispatch(
      updateWorkedHistoryPagination({
        ...workedHistoryPagination,
        currentPage: 1,
        isLastPage: false,
      }),
    );
    dispatch(setLoading(true));
    dispatch(fetchWorkHistory());
  };

  return (
    <div className="WorkHistoryListing_Wrapper flex-1" ref={wrapper}>
      <div
        ref={dataTableRef}
        className="DataTable_Wrapper hide_extended_arrow w-[50vw]"
      >
        <DataTable
          columns={columns}
          data={candidateWorkedHistory}
          noDataComponent={<TableNoContent />}
          clearSelectedRows={toggleListingSelection}
          selectableRows
          onSort={handleSort}
          onSelectedRowsChange={(state) => {
            let selectedContracts = state.selectedRows.map((item) => {
              return item.contract_id;
            });
            dispatch(updateSelectedWorkedHistory(selectedContracts));
          }}
          progressPending={isLoading}
          progressComponent={<Skeleton active paragraph={{ rows: 1 }} />}
          fixedHeader
          fixedHeaderScrollHeight="700px"
          className="_DataTable h-[calc(100vh-40vh)] !min-h-[522px]"
          expandableRows={workedHistoryView !== "shifts"}
          expandOnRowClicked
          expandableRowsComponent={Expandedrow}
          onRowExpandToggled={(state, row) => {
            if (state) {
              dispatch(
                fetchExpandedWorkHistory({
                  contractId: row.contract_id,
                  //     candidate_id: row.candidate_id,
                }),
              );
            }
          }}
        />
      </div>
    </div>
  );
};

export default WorkHistoryListing;
